import { Object3D, Vector3 } from 'three';
import { ProjectRenderer } from '../ProjectRenderer';
import { Measurement } from '../objects/Measurement';
import { TotalHeightMeasurement } from '../objects/TotalHeightMeasurement';
import {
	MeasurementOffsetAxis,
	MeasurementOffsetLabel,
	MeasurementPointPosition,
	UserDataProp,
} from '../types';
import RENDERER_CONFIG from '@/configs/rendererConfig';

export class MeasurementManager {
	private _visible = false;
	measurements: Measurement[] = [];

	constructor(private renderer: ProjectRenderer) {}

	get visible() {
		return this._visible;
	}

	set visible(value: boolean) {
		this._visible = value;

		for (const measurement of this.measurements) {
			measurement.visible = value;
		}
	}

	private getLabelRotation(rotation: number | undefined) {
		const { HORIZONTAL_LABEL_ROTATION } = RENDERER_CONFIG.MEASUREMENT;

		if (rotation === undefined || rotation === HORIZONTAL_LABEL_ROTATION)
			return 0;

		return rotation - HORIZONTAL_LABEL_ROTATION;
	}

	initialize(measurementPoints: Object3D[]) {
		this.clear();

		const pointsById = new Map<string, Object3D[]>();

		for (const point of measurementPoints) {
			const id: string = point.userData[UserDataProp.MEASUREMENT_ID];
			const points = pointsById.get(id) ?? [];
			points.push(point);
			pointsById.set(id, points);
		}

		for (const [id, points] of pointsById) {
			const start = points.find(
				(point) =>
					point.userData[UserDataProp.MEASUREMENT_POINT_POSITION] ===
					MeasurementPointPosition.START
			);
			const end = points.find(
				(point) =>
					point.userData[UserDataProp.MEASUREMENT_POINT_POSITION] ===
					MeasurementPointPosition.END
			);

			if (points.length !== 2 || !start || !end) {
				console.warn(`INVALID_MEASUREMENT_POINTS: ${id}`);
				continue;
			}

			const label: string = start.userData[UserDataProp.MEASUREMENT_LABEL],
				offsetAxis: MeasurementOffsetAxis =
					start.userData[UserDataProp.MEASUREMENT_OFFSET_AXIS],
				offsetLength: number | undefined =
					start.userData[UserDataProp.MEASUREMENT_OFFSET_LENGTH],
				offsetLabel: MeasurementOffsetLabel | undefined =
					start.userData[UserDataProp.MEASUREMENT_OFFSET_LABEL],
				labelRotation: number | undefined =
					start.userData[UserDataProp.MEASUREMENT_LABEL_ROTATION];

			const labelOffset = offsetLabel
				? new Vector3(...offsetLabel).divideScalar(1000)
				: new Vector3();

			const measurement = new Measurement(this.renderer, {
				id,
				label,
				offsetAxis,
				offsetLength:
					offsetLength !== undefined ? offsetLength / 1000 : undefined,
				labelOffset,
				labelRotation: this.getLabelRotation(labelRotation),
				points: [start, end],
			});

			this.measurements.push(measurement);
		}

		this.measurements.push(new TotalHeightMeasurement(this.renderer));
	}

	updateMeasurements() {
		for (const measurement of this.measurements) {
			measurement.draw();
		}
	}

	clear() {
		for (const measurement of this.measurements) {
			measurement.dispose();
		}

		this.measurements = [];
	}
}
